/**
 * VENDOR MODULES
 */
import React from 'react';
import { useTranslation } from "react-i18next";

import EVENT_NAMES from 'shared/constants/events';
import Emitter from 'shared/events/emitter';

import {
  calculateDutchingPreview,
  calculateSystemPreview,
  getTicketSlipResolvedType,
  hasDuplicateEvents,
} from 'shared/utils/ticket-slip-helper';
import { useTicketSlipStore } from 'features/tickets/store';
import { useCreateTicketMutation, useCreditAvailableQuery, ticketsQueryKeys } from 'features/tickets/queries';
import { useQueryClient } from '@tanstack/react-query';
import { isGuestUserSession, useUserSessionContext } from 'features/session/userSessionStore';
import { notifyHostBalanceChanged } from 'features/session/hostBalanceBridge';
import { cn } from 'lib/utils';
import { formatCurrency } from 'shared/money/formatCurrency';

/**
 * Modern Confirm Bet Button Component
 *
 * Refactored confirm button with modern styling
 */
function ModernConfirmBetButton() {
  const { t, i18n } = useTranslation();
  const ticket_slip = useTicketSlipStore((s) => s.ticket_slip);
  const total_stake = useTicketSlipStore((s) => s.total_stake);
  const bet_mode = useTicketSlipStore((s) => s.bet_mode);
  const stake_mode = useTicketSlipStore((s) => s.stake_mode);
  const system_rows = useTicketSlipStore((s) => s.system_rows);
  const removeAllTips = useTicketSlipStore((s) => s.removeAllTips);
  const setLoadingSlip = useTicketSlipStore((s) => s.setLoading);
  const setBetslipTab = useTicketSlipStore((s) => s.setBetslipTab);
  const setNewlyPlacedTicketId = useTicketSlipStore((s) => s.setNewlyPlacedTicketId);
  const createTicketMutation = useCreateTicketMutation();
  const queryClient = useQueryClient();
  const [{ user }, userDispatch] = useUserSessionContext();
  const ticket = (createTicketMutation.data || {}) as { ticket_id?: string | number; new_player_balance?: number };
  const loading = createTicketMutation.isPending;

  // Check if user is a guest
  const isGuest = isGuestUserSession(user);
  // Balance from platform (request-credit). Only fetch when logged in.
  const { data: creditData } = useCreditAvailableQuery({ enabled: !isGuest });
  const creditAvailable = creditData?.credit_available != null
    ? Number(creditData.credit_available)
    : undefined;
  // Use platform balance when available, else fallback to user (e.g. after createTicket response).
  // Display default when credit not yet loaded (single documented fallback).
  const balance = creditAvailable ?? Number(user?.player_balance ?? 0);
  const systemPreview = calculateSystemPreview(ticket_slip, system_rows);
  const dutchingPreview = calculateDutchingPreview(ticket_slip);
  const resolvedType = getTicketSlipResolvedType(ticket_slip, bet_mode, system_rows, stake_mode);

  /**
   * Called on confirm bet. This here is the confirmTicket logic
   */
  const onConfirmBet = () => {
    // If guest, show login prompt instead of placing bet
    if (isGuest) {
      Emitter.emit(EVENT_NAMES.DIALOG.SHOW, {
        title: t('LOGIN_REQUIRED_TITLE') || 'Login Required',
        message: t('LOGIN_REQUIRED_MESSAGE') || 'Please login to place bets. You can continue browsing as a guest.',
      });
      return false;
    }

    if (loading) {
      return true;
    }

    // validate if there's bet
    if (!ticket_slip.length) {
      Emitter.emit(EVENT_NAMES.DIALOG.SHOW, {
        title: t('ERROR_NO_BET_TITLE'),
        message: t('ERROR_NO_BET_MESSAGE'),
      });
      return false;
    }

    if (resolvedType === 'SYSTEM' && !systemPreview.valid) {
      Emitter.emit(EVENT_NAMES.DIALOG.SHOW, {
        title: t('SYSTEM_INVALID_TITLE', 'SYSTEM invalid'),
        message: t(
          'SYSTEM_INVALID_ZERO_EFFECTIVE_COMBINATIONS',
          'At least one active SYSTEM row has no valid combinations. Adjust the rows or selections before confirming.'
        ),
      });
      return false;
    }

    if (resolvedType === 'MULTIPLE' && hasDuplicateEvents(ticket_slip)) {
      Emitter.emit(EVENT_NAMES.DIALOG.SHOW, {
        title: t('ERROR_MULTIPLE_TITLE', 'Invalid multiple bet'),
        message: t(
          'ERROR_MULTIPLE_MESSAGE',
          'MULTIPLE does not allow multiple selections from the same event. Edit individual leg stakes to switch to DUTCHING.'
        ),
      });
      return false;
    }

    const activeSystemRows = system_rows
      .map((row) => ({
        size: Number(row.size),
        stake_per_line: Number(row.stake_per_line),
      }))
      .filter((row) => Number.isInteger(row.size) && Number.isFinite(row.stake_per_line) && row.stake_per_line > 0);

    // validate total stake (increment 0.50, minimum > 0)
    if (
      (resolvedType === 'SINGLE' || resolvedType === 'MULTIPLE') &&
      (!Number.isFinite(total_stake) || total_stake <= 0 || total_stake % 0.5 !== 0)
    ) {
      Emitter.emit(EVENT_NAMES.DIALOG.SHOW, {
        title: t('ERROR_AMOUNT_TITLE'),
        message: t('ERROR_AMOUNT_MESSAGE', { min_stake: formatCurrency(0.5, user?.currency, i18n?.language) }),
      });
      return false;
    }

    if (resolvedType === 'DUTCHING') {
      const invalidTipStake = ticket_slip.some((tip) => {
        const stake = Number(tip.stake);
        return !Number.isFinite(stake) || stake <= 0 || stake % 0.5 !== 0;
      });
      if (!dutchingPreview.valid || invalidTipStake) {
        Emitter.emit(EVENT_NAMES.DIALOG.SHOW, {
          title: t('ERROR_AMOUNT_TITLE'),
          message: t('ERROR_AMOUNT_MESSAGE', { min_stake: formatCurrency(0.5, user?.currency, i18n?.language) }),
        });
        return false;
      }
    }

    if (resolvedType === 'SYSTEM' && activeSystemRows.length === 0) {
      Emitter.emit(EVENT_NAMES.DIALOG.SHOW, {
        title: t('SYSTEM_STAKE_REQUIRED_TITLE', 'SYSTEM stake required'),
        message: t('SYSTEM_STAKE_REQUIRED_MESSAGE', 'Set a positive stake on at least one SYSTEM row before confirming.'),
      });
      return false;
    }

    if (resolvedType === 'SYSTEM' && activeSystemRows.some((row) => row.stake_per_line % 0.5 !== 0)) {
      Emitter.emit(EVENT_NAMES.DIALOG.SHOW, {
        title: t('ERROR_AMOUNT_TITLE'),
        message: t('ERROR_AMOUNT_MESSAGE', { min_stake: formatCurrency(0.5, user?.currency, i18n?.language) }),
      });
      return false;
    }

    const amountToCheck =
      resolvedType === 'SYSTEM'
        ? systemPreview.totalStake
        : resolvedType === 'DUTCHING'
          ? dutchingPreview.totalStake
          : total_stake;
    if (amountToCheck > balance) {
      Emitter.emit(EVENT_NAMES.DIALOG.SHOW, {
        title: t('INSUFFICIENT_BALANCE_TITLE', 'Insufficient Balance'),
        message: t('INSUFFICIENT_BALANCE_MESSAGE', 'Your balance is not enough to place this bet.'),
      });
      return false;
    }

    const playerId = user?.player_id;
    if (playerId == null || String(playerId).trim() === '') {
      Emitter.emit(EVENT_NAMES.DIALOG.SHOW, {
        title: t('LOGIN_REQUIRED_TITLE') || 'Login Required',
        message: t('LOGIN_REQUIRED_MESSAGE') || 'Please login to place bets.',
      });
      return false;
    }

    createTicketMutation.mutate({
      ticket_slip,
      total_stake: amountToCheck,
      player_id: playerId,
      bet_type: resolvedType,
      ...(resolvedType === 'SYSTEM'
        ? {
            system_rows: activeSystemRows,
          }
        : {}),
    });
  };

  const onTicketSuccess = () => {
    if (ticket.ticket_id) {
      removeAllTips();
      setNewlyPlacedTicketId(Number(ticket.ticket_id));
      setBetslipTab('mybets');

      Emitter.emit('TOAST.SHOW', {
        message: t('BET_PLACED_SUCCESS'),
        variant: 'success',
        durationMs: 4000,
      });

      const new_user_data = {
        ...(user ?? {}),
        player_balance: ticket.new_player_balance
      };
      userDispatch({ type: 'SET_USER', payload: new_user_data });
      notifyHostBalanceChanged(ticket.new_player_balance);
      queryClient.invalidateQueries({ queryKey: ticketsQueryKeys.creditAvailable });
      createTicketMutation.reset();
      Emitter.emit(EVENT_NAMES.PLATFORM.SESSIONS_PREPAID.READ_PROCEED, {});
    }
  };

  React.useEffect(onTicketSuccess, [loading === false && !!ticket.ticket_id]);
  React.useEffect(() => {
    setLoadingSlip(loading);
  }, [loading]);

  return (
    <button
      className={cn(
        "w-full flex items-center justify-center gap-2 py-3.5 px-4 rounded-xl font-bold text-sm uppercase tracking-wide transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2",
        loading
          ? "bg-muted text-muted-foreground cursor-not-allowed"
          : isGuest
            ? "bg-secondary/20 text-black border-2 border-secondary/40 cursor-not-allowed"
            : "bg-neutral text-neutral-foreground hover:bg-neutral/90 focus:ring-neutral shadow-md hover:shadow-lg"
      )}
      onClick={onConfirmBet}
      disabled={loading || !ticket_slip.length || isGuest === true}
      title={isGuest ? (t('LOGIN_REQUIRED_MESSAGE') || 'Please login to place bets') : undefined}
    >
      {loading ? (
        <>
          <div className="w-4 h-4 border-2 border-muted-foreground border-t-transparent rounded-full animate-spin" />
          {t('PROCESSING')}
        </>
      ) : isGuest ? (
        <>
          <i className="bi bi-lock text-lg" />
          {t('LOGIN_TO_BET') || 'Login to Bet'}
        </>
      ) : (
        <>
          <i className="bi bi-check-circle text-lg" />
          {t('CONFIRM_BET')}
        </>
      )}
    </button>
  );
}

export default ModernConfirmBetButton;

